<template>
  <div class="help-root">

    <div class="help-intro">
      <span class="material-symbols-outlined help-hero-icon">dentistry</span>
      <p>
        Registra el estado de cada pieza dental del paciente, calcula
        automáticamente los índices CPO y permite documentar movilidad,
        recesión y notas clínicas por pieza.
      </p>
    </div>

    <!-- Modos de trabajo -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">toggle_on</span>
        Modos de trabajo
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in modos" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Estados de las piezas -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">palette</span>
        Estados de las piezas dentales
      </h3>
      <div class="help-estados">
        <span v-for="e in estados" :key="e.label"
          class="help-estado-badge"
          :style="{ background: e.color, color: '#fff' }">
          {{ e.simbolo ? e.simbolo + ' ' : '' }}{{ e.label }}
        </span>
      </div>
      <p class="help-card-desc" style="padding:0 0.25rem;margin-top:0.5rem">
        Cada estado se muestra con un color y un símbolo dentro de la pieza
        dental. La leyenda de colores aparece debajo del odontograma.
      </p>
    </section>

    <!-- Índices CPO -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">calculate</span>
        Índices CPO / ceo
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in indices" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Panel de detalle por pieza -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">info</span>
        Panel de detalle por pieza
      </h3>
      <p class="help-card-desc" style="padding:0 0.25rem;margin-bottom:0.5rem">
        Al hacer clic en cualquier pieza dental aparece un panel con campos
        adicionales editables en Modo Edición:
      </p>
      <div class="help-cards">
        <div class="help-card" v-for="item in detalle" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Acciones -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">touch_app</span>
        Acciones disponibles
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="a in acciones" :key="a.titulo">
          <span class="material-symbols-outlined help-action-icon" :style="{ color: a.color }">{{ a.icon }}</span>
          <div>
            <p class="help-card-title">{{ a.titulo }}</p>
            <p class="help-card-desc">{{ a.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Restricción -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">lock</span>
        Restricciones por estado de la atención
      </h3>
      <div class="help-estados" style="margin-bottom:0.5rem">
        <span class="help-estado-badge" style="background:#fff3cd;color:#856404">PENDIENTE</span>
        <span class="help-estado-badge" style="background:#cce5ff;color:#004085">PROCESO</span>
      </div>
      <p class="help-card-desc" style="padding:0 0.25rem">
        El Modo Edición, los botones Guardar y Limpiar, y los campos del
        panel de detalle solo están habilitados cuando la atención está en
        estado <strong>PENDIENTE</strong> o <strong>PROCESO</strong>. En
        cualquier otro estado el componente abre directamente en
        <strong>Modo Consulta</strong>.
      </p>
    </section>

    <div class="help-tip">
      <span class="material-symbols-outlined">lightbulb</span>
      <p>
        Los índices CPO se recalculan automáticamente cada vez que cambias
        el estado de una pieza. No es necesario guardar para ver el índice
        actualizado. El botón <strong>Guardar</strong> persiste todos los
        cambios, incluidos los del panel de detalle.
      </p>
    </div>

  </div>
</template>

<script>
export default {
  name: "AyudaOdontograma",
  data() {
    return {
      modos: [
        {
          icon: "brush",
          titulo: "Modo Edición",
          desc:   "Permite modificar el estado de las piezas dentales. Selecciona un estado de la barra de colores y haz clic en la pieza para aplicarlo. Solo disponible en atenciones PENDIENTE o PROCESO.",
        },
        {
          icon: "pageview",
          titulo: "Modo Consulta",
          desc:   "Solo permite visualizar el odontograma y consultar el detalle de cada pieza haciendo clic en ella. No modifica ningún dato. Disponible en cualquier estado.",
        },
      ],
      estados: [
        { label: "Sano",            color: "#27ae60", simbolo: ""  },
        { label: "Caries",          color: "#e74c3c", simbolo: "C" },
        { label: "Obturado",        color: "#2980b9", simbolo: "O" },
        { label: "Extracción ind.", color: "#c0392b", simbolo: "X" },
        { label: "Extraído/caries", color: "#7f8c8d", simbolo: "X" },
        { label: "Extraído/otro",   color: "#95a5a6", simbolo: "X" },
        { label: "Endodoncia ind.", color: "#e67e22", simbolo: "▲" },
        { label: "Endodoncia real.",color: "#1abc9c", simbolo: "▲" },
        { label: "Sellante ind.",   color: "#9b59b6", simbolo: "*" },
        { label: "Corona",          color: "#f39c12", simbolo: "◇" },
      ],
      indices: [
        {
          icon: "emergency",
          titulo: "C — Cariadas",
          desc:   "Cuenta las piezas con estado Caries o Endodoncia indicada.",
        },
        {
          icon: "remove_circle",
          titulo: "P — Perdidas",
          desc:   "Cuenta las piezas con estado Extraído por caries o Extraído por otra causa.",
        },
        {
          icon: "check_circle",
          titulo: "O — Obturadas",
          desc:   "Cuenta las piezas con estado Obturado, Corona o Endodoncia realizada.",
        },
        {
          icon: "calculate",
          titulo: "CPOD Total",
          desc:   "Suma de C + P + O. Indica el nivel de experiencia de caries en la dentición permanente del paciente.",
        },
      ],
      detalle: [
        {
          icon: "swap_vert",
          titulo: "Estado",
          desc:   "Permite cambiar el estado de la pieza directamente desde el panel sin necesidad de hacer clic en el odontograma.",
        },
        {
          icon: "trending_up",
          titulo: "Movilidad (1–3)",
          desc:   "Grado de movilidad dental según el índice modificado de Miller: 1 leve, 2 moderada, 3 severa. Aplica solo en dentición definitiva.",
        },
        {
          icon: "vertical_align_bottom",
          titulo: "Recesión (1–4)",
          desc:   "Grado de recesión gingival según la clasificación de Miller: de 1 (leve) a 4 (severa). Aplica solo en dentición definitiva.",
        },
        {
          icon: "text_snippet",
          titulo: "Notas",
          desc:   "Campo libre para registrar observaciones clínicas adicionales sobre la pieza seleccionada. Máximo 500 caracteres.",
        },
      ],
      acciones: [
        {
          icon:   "dentistry",
          color:  "#0059cc",
          titulo: "Clic en pieza dental",
          desc:   "En Modo Edición: aplica el estado seleccionado a la pieza y abre su panel de detalle. En Modo Consulta: solo muestra el detalle sin modificar nada.",
        },
        {
          icon:   "restart_alt",
          color:  "#dc3545",
          titulo: "Limpiar",
          desc:   "Restablece todas las piezas a estado Sano y borra los campos de movilidad, recesión y notas. Solo disponible en Modo Edición con atención PENDIENTE o PROCESO.",
        },
        {
          icon:   "save",
          color:  "#198754",
          titulo: "Guardar",
          desc:   "Persiste el estado de todas las piezas, los índices CPO y los datos de detalle en el servidor. Solo disponible en Modo Edición con atención PENDIENTE o PROCESO.",
        },
      ],
    };
  },
};
</script>

<style src="@/assets/css/ayuda.css" scoped></style>